// Plain data model behind the Live Map, plus the pure functions that derive what
// a dispatcher needs to "status" a job: which trucks are checked in, where each
// truck is relative to the job sites, and which site it just came from.
// Kept free of Firestore and Google Maps types so it can be unit tested.
import { minBy } from 'lodash';

import {
  LatLngLiteral,
  getBearingDegrees,
  getDistanceMeters,
  getHeadingFromPath,
  isPointInPolygon,
} from '@/components/LiveMap/geo';

export type ReceiverStatus =
  | 'not notified'
  | 'notified'
  | 'accepted'
  | 'acceptedNotCheckedIn'
  | 'declined'
  | 'checkedIn'
  | 'checkedOut';

export type SiteRole = 'load' | 'dump' | 'stop';

export type LiveMapSite = LatLngLiteral & {
  id: string;
  name: string;
  address: string | null;
  order: number;
  role: SiteRole;
  label: string; // "A", "B", ... by order, matching react-app's site markers
  radiusMeters: number;
  innerRadiusMeters: number | null;
  polygon: LatLngLiteral[] | null; // Drawn geofence; takes precedence over the radius
};

export type LiveMapJob = {
  id: string;
  name: string;
  clientName: string;
  startDatetime: Date;
  totalTrucks: number;
  color: string;
  sites: LiveMapSite[];
};

export type TruckAssignment = {
  assignmentId: string;
  jobId: string;
  status: ReceiverStatus;
  taskId: string | null;
};

export type LiveMapOperator = {
  uid: string;
  name: string;
  nickname: string;
  companyName: string;
  truckDisplayId: string | null;
  assignments: TruckAssignment[];
  isSimulated: boolean;
};

export type GeotrackPoint = LatLngLiteral & {
  timestamp: number;
  heading: number | null;
  isMoving: boolean;
};

export type TruckLocation = {
  uid: string;
  latest: GeotrackPoint;
  recent: GeotrackPoint[]; // Oldest to newest
  heading: number | null;
};

export type TruckActivity =
  | { kind: 'noSignal' }
  | { kind: 'atSite'; site: LiveMapSite; job: LiveMapJob }
  | {
      kind: 'enRoute';
      towardSite: LiveMapSite | null;
      fromSite: LiveMapSite | null;
    }
  | { kind: 'parked' };

// Site geofence used when the job's site snapshot has no radius (react-app's DEFAULT_SITE_RADIUS, 1/4 mile)
export const DEFAULT_SITE_RADIUS_METERS = 402.34;
// A fix older than this means the phone stopped reporting (same as react-app's ACTIVE_USER_TIME_LAPSE_MS)
export const LOCATION_STALE_AFTER_MS = 5 * 60 * 1000;
// Max angle between a truck's heading and the bearing to a site to call it "heading toward" that site
const TOWARD_SITE_MAX_ANGLE_DEGREES = 50;

// Color-blind friendly categorical palette, dark enough for white arrows on top
export const JOB_COLORS = [
  '#1877cd',
  '#d9831f',
  '#095d40',
  '#9b479f',
  '#b21f28',
  '#0098cd',
  '#6a1b32',
  '#4b869f',
  '#7a6a00',
  '#3d4451',
] as const;

const STATUS_RANK: Record<ReceiverStatus, number> = {
  declined: 0,
  'not notified': 1,
  notified: 2,
  accepted: 3,
  acceptedNotCheckedIn: 4,
  checkedIn: 5,
  checkedOut: 6,
};

/**
 * Pick a stable color for the n-th job.
 * @param {number} index
 * @returns {string} Hex color.
 */
export function getJobColor(index: number): string {
  return JOB_COLORS[index % JOB_COLORS.length] as string;
}

/**
 * Letter label for a site position: 0 -> "A", 1 -> "B", ...
 * @param {number} index
 * @returns {string}
 */
export function getSiteLabel(index: number): string {
  return String.fromCharCode(65 + (index % 26));
}

type JobSiteSnapshot = {
  lat?: number;
  lng?: number;
  order?: number;
  name?: string;
  address?: string;
  radius?: number;
  innerRadius?: number;
  polygonGeofence?: Array<{ lat?: number; lng?: number }> | null;
};

/**
 * Keep a site's drawn geofence only when it is a usable polygon.
 * @param {JobSiteSnapshot['polygonGeofence']} polygon
 * @returns {LatLngLiteral[] | null}
 */
function getValidPolygon(
  polygon: JobSiteSnapshot['polygonGeofence']
): LatLngLiteral[] | null {
  const points = (polygon || []).filter(
    (point): point is LatLngLiteral =>
      Number.isFinite(point?.lat) && Number.isFinite(point?.lng)
  );
  return points.length >= 3 ? points : null;
}

/**
 * Turn a Job's denormalized `sites` map into an ordered list of mappable sites.
 * The first site (order 0) is where trucks load and the last is where they dump,
 * the same convention react-app uses for origin and destination.
 * @param {Record<string, JobSiteSnapshot> | undefined} sitesMap - `Job.sites`, keyed by site id.
 * @returns {LiveMapSite[]}
 */
export function getJobSites(
  sitesMap: Record<string, JobSiteSnapshot> | undefined
): LiveMapSite[] {
  const sites = Object.entries(sitesMap || {})
    .filter(
      ([, site]) => Number.isFinite(site?.lat) && Number.isFinite(site?.lng)
    )
    .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0));
  return sites.map(([id, site], index) => ({
    id,
    name: site.name || 'Unnamed site',
    address: site.address || null,
    lat: site.lat as number,
    lng: site.lng as number,
    order: site.order ?? index,
    role:
      index === 0
        ? 'load'
        : index === sites.length - 1
          ? 'dump'
          : ('stop' as SiteRole),
    label: getSiteLabel(index),
    radiusMeters:
      !!site.radius && site.radius > 0
        ? site.radius
        : DEFAULT_SITE_RADIUS_METERS,
    innerRadiusMeters:
      !!site.innerRadius && site.innerRadius > 0 ? site.innerRadius : null,
    polygon: getValidPolygon(site.polygonGeofence),
  }));
}

/**
 * True for statuses where the operator has committed to the job and is expected
 * to be driving it today (accepted but not yet checked in, or checked in).
 * @param {ReceiverStatus} status
 * @returns {boolean}
 */
export function getIsActiveAssignmentStatus(status: ReceiverStatus): boolean {
  return (
    status === 'accepted' ||
    status === 'acceptedNotCheckedIn' ||
    status === 'checkedIn'
  );
}

/**
 * The operator's assignments they are currently working or about to work,
 * checked-in ones first.
 * @param {LiveMapOperator} operator
 * @returns {TruckAssignment[]}
 */
export function getActiveAssignments(
  operator: LiveMapOperator
): TruckAssignment[] {
  return operator.assignments
    .filter((assignment) => getIsActiveAssignmentStatus(assignment.status))
    .sort((a, b) => STATUS_RANK[b.status] - STATUS_RANK[a.status]);
}

/**
 * The job that drives the truck's color and context: the checked-in one, else
 * the first accepted one.
 * @param {LiveMapOperator} operator
 * @returns {string | null} Job id.
 */
export function getPrimaryJobId(operator: LiveMapOperator): string | null {
  return getActiveAssignments(operator)[0]?.jobId ?? null;
}

/**
 * Build a truck's location state from raw fixes in any order. While the phone
 * says the truck is moving, prefers the heading it reports and falls back to the
 * bearing between recent fixes. A stopped truck has no heading: its recent fixes
 * still hold the drive in, which would otherwise make it look en route.
 * @param {string} uid
 * @param {GeotrackPoint[]} points
 * @returns {TruckLocation | null} Null when there are no fixes.
 */
export function buildTruckLocation(
  uid: string,
  points: GeotrackPoint[]
): TruckLocation | null {
  const recent = [...points].sort((a, b) => a.timestamp - b.timestamp);
  const latest = recent[recent.length - 1];
  if (!latest) return null;
  const reportedHeading =
    latest.isMoving && latest.heading !== null && latest.heading >= 0
      ? latest.heading
      : null;
  return {
    uid,
    latest,
    recent,
    heading: latest.isMoving
      ? (reportedHeading ?? getHeadingFromPath(recent))
      : null,
  };
}

/**
 * Whether the latest fix is too old to trust as a live position.
 * @param {TruckLocation | undefined} location
 * @param {number} [nowMs=Date.now()]
 * @returns {boolean}
 */
export function getIsLocationStale(
  location: TruckLocation | undefined,
  nowMs: number = Date.now()
): boolean {
  return (
    !location || nowMs - location.latest.timestamp > LOCATION_STALE_AFTER_MS
  );
}

/**
 * Whether a position is inside a site's geofence: its drawn polygon when it has
 * one, otherwise the circle around its center (same rule react-app draws).
 * @param {LatLngLiteral} position
 * @param {LiveMapSite} site
 * @returns {boolean}
 */
export function getIsInsideSite(
  position: LatLngLiteral,
  site: LiveMapSite
): boolean {
  return site.polygon
    ? isPointInPolygon(position, site.polygon)
    : getDistanceMeters(position, site) <= site.radiusMeters;
}

/**
 * Find the job site whose geofence contains a position, if any. When geofences
 * overlap, the site whose center is closest wins.
 * @param {LatLngLiteral} position
 * @param {LiveMapJob[]} jobs - Jobs whose sites should be checked.
 * @returns {{ site: LiveMapSite, job: LiveMapJob } | null}
 */
export function findSiteAtPosition(
  position: LatLngLiteral,
  jobs: LiveMapJob[]
): { site: LiveMapSite; job: LiveMapJob } | null {
  const matches = jobs.flatMap((job) =>
    job.sites
      .filter((site) => getIsInsideSite(position, site))
      .map((site) => ({ site, job }))
  );
  return (
    minBy(matches, ({ site }) => getDistanceMeters(position, site)) || null
  );
}

/**
 * The job sites a truck went through, in order, collapsing consecutive fixes
 * inside the same site into one visit. Time spent between sites is ignored, so
 * A, (road), C reads as [A, C].
 * @param {GeotrackPoint[]} path - Fixes ordered oldest to newest.
 * @param {LiveMapSite[]} sites
 * @returns {string[]} Site ids in visit order.
 */
export function getSiteVisits(
  path: GeotrackPoint[],
  sites: LiveMapSite[]
): string[] {
  const visits: string[] = [];
  path.forEach((point) => {
    const site = minBy(
      sites.filter((candidate) => getIsInsideSite(point, candidate)),
      (candidate) => getDistanceMeters(point, candidate)
    );
    if (site && visits[visits.length - 1] !== site.id) visits.push(site.id);
  });
  return visits;
}

export type SiteTrip = {
  fromSiteId: string;
  toSiteId: string;
  count: number; // How many times a truck drove this leg today
};

/**
 * Site-to-site legs trucks actually drove, from their GPS paths. Used to draw
 * lanes for jobs with more than two sites, where the order of travel can't be
 * assumed from the site letters.
 * @param {GeotrackPoint[][]} paths - One path per truck, each oldest to newest.
 * @param {LiveMapSite[]} sites - The job's sites.
 * @returns {SiteTrip[]} Most-driven legs first.
 */
export function getSiteTrips(
  paths: GeotrackPoint[][],
  sites: LiveMapSite[]
): SiteTrip[] {
  const tripsByKey: Record<string, SiteTrip> = {};
  paths.forEach((path) => {
    const visits = getSiteVisits(path, sites);
    visits.slice(1).forEach((toSiteId, idx) => {
      const fromSiteId = visits[idx] as string;
      const key = `${fromSiteId}>${toSiteId}`;
      tripsByKey[key] = tripsByKey[key] || { fromSiteId, toSiteId, count: 0 };
      tripsByKey[key].count++;
    });
  });
  return Object.values(tripsByKey).sort((a, b) => b.count - a.count);
}

/**
 * Merge two lists of fixes into one path ordered oldest to newest, dropping
 * duplicates (e.g. today's history plus the live tail).
 * @param {GeotrackPoint[]} a
 * @param {GeotrackPoint[]} b
 * @returns {GeotrackPoint[]}
 */
export function mergePaths(
  a: GeotrackPoint[],
  b: GeotrackPoint[]
): GeotrackPoint[] {
  const byKey = new Map<string, GeotrackPoint>();
  [...a, ...b].forEach((point) =>
    byKey.set(`${point.timestamp}:${point.lat}:${point.lng}`, point)
  );
  return [...byKey.values()].sort((x, y) => x.timestamp - y.timestamp);
}

/**
 * Walk a truck's path backwards to find the last job site it was inside and
 * when it left. Answers "where did this truck just come from?".
 * @param {GeotrackPoint[]} path - Fixes ordered oldest to newest.
 * @param {LiveMapJob[]} jobs - Jobs the truck is working.
 * @returns {{ site: LiveMapSite, job: LiveMapJob, leftAt: number | null } | null}
 *   `leftAt` is null when the truck is still inside the site.
 */
export function findLastVisitedSite(
  path: GeotrackPoint[],
  jobs: LiveMapJob[]
): { site: LiveMapSite; job: LiveMapJob; leftAt: number | null } | null {
  let firstPointAfterSite: GeotrackPoint | null = null;
  for (let idx = path.length - 1; idx >= 0; idx--) {
    const point = path[idx];
    if (!point) continue;
    const match = findSiteAtPosition(point, jobs);
    if (match) {
      return {
        ...match,
        leftAt: firstPointAfterSite?.timestamp ?? null,
      };
    }
    firstPointAfterSite = point;
  }
  return null;
}

/**
 * Smallest absolute difference between two compass angles.
 * @param {number} a - Degrees.
 * @param {number} b - Degrees.
 * @returns {number} Degrees in [0, 180].
 */
export function getAngleDifference(a: number, b: number): number {
  const diff = Math.abs(a - b) % 360;
  return diff > 180 ? 360 - diff : diff;
}

/**
 * Guess which job site a moving truck is driving toward from its heading.
 * @param {LatLngLiteral} position
 * @param {number | null} heading - Degrees.
 * @param {LiveMapSite[]} sites
 * @param {string | null} [excludeSiteId] - Usually the site it just left.
 * @returns {LiveMapSite | null}
 */
export function findSiteAhead(
  position: LatLngLiteral,
  heading: number | null,
  sites: LiveMapSite[],
  excludeSiteId: string | null = null
): LiveMapSite | null {
  if (heading === null) return null;
  const ahead = sites
    .filter((site) => site.id !== excludeSiteId)
    .map((site) => ({
      site,
      angle: getAngleDifference(heading, getBearingDegrees(position, site)),
      distance: getDistanceMeters(position, site),
    }))
    .filter(({ angle }) => angle <= TOWARD_SITE_MAX_ANGLE_DEGREES)
    .sort((a, b) => a.angle - b.angle || a.distance - b.distance);
  return ahead[0]?.site ?? null;
}

/**
 * Classify what a truck is doing right now relative to its jobs' sites.
 * @param {TruckLocation | undefined} location
 * @param {LiveMapJob[]} jobs - The truck's active jobs.
 * @param {GeotrackPoint[]} [path] - Longer history (e.g. today's trail) to find the site it came from.
 * @returns {TruckActivity}
 */
export function getTruckActivity(
  location: TruckLocation | undefined,
  jobs: LiveMapJob[],
  path?: GeotrackPoint[]
): TruckActivity {
  if (!location) return { kind: 'noSignal' };
  const atSite = findSiteAtPosition(location.latest, jobs);
  if (atSite) return { kind: 'atSite', ...atSite };
  const lastVisited = findLastVisitedSite(path || location.recent, jobs);
  if (!location.latest.isMoving) {
    return { kind: 'parked' };
  }
  const sites = jobs.flatMap((job) => job.sites);
  return {
    kind: 'enRoute',
    fromSite: lastVisited?.site ?? null,
    towardSite: findSiteAhead(
      location.latest,
      location.heading,
      sites,
      lastVisited?.site.id ?? null
    ),
  };
}

export type JobStatusSummary = {
  requested: number;
  assigned: number;
  accepted: number; // Accepted, not yet checked in
  checkedIn: number;
  checkedOut: number;
  declined: number;
  pending: number; // Notified or not notified, no answer yet
  onSiteBySiteId: Record<string, number>;
  enRoute: number;
  noSignal: number; // Checked in but no fresh location
};

/**
 * Count where every truck on a job stands, so a dispatcher can tell at a glance
 * whether all the trucks they expect have shown up and checked in.
 * @param {LiveMapJob} job
 * @param {LiveMapOperator[]} operators - Operators with at least one assignment on the job.
 * @param {Record<string, TruckLocation>} locations - Keyed by operator uid.
 * @param {number} [nowMs=Date.now()]
 * @returns {JobStatusSummary}
 */
export function getJobStatusSummary(
  job: LiveMapJob,
  operators: LiveMapOperator[],
  locations: Record<string, TruckLocation>,
  nowMs: number = Date.now()
): JobStatusSummary {
  const summary: JobStatusSummary = {
    requested: job.totalTrucks,
    assigned: 0,
    accepted: 0,
    checkedIn: 0,
    checkedOut: 0,
    declined: 0,
    pending: 0,
    onSiteBySiteId: {},
    enRoute: 0,
    noSignal: 0,
  };
  operators.forEach((operator) => {
    operator.assignments
      .filter((assignment) => assignment.jobId === job.id)
      .forEach((assignment) => {
        const { status } = assignment;
        if (status === 'declined') {
          summary.declined++;
          return;
        }
        summary.assigned++;
        if (status === 'checkedOut') summary.checkedOut++;
        else if (status === 'checkedIn') summary.checkedIn++;
        else if (getIsActiveAssignmentStatus(status)) summary.accepted++;
        else summary.pending++;

        if (status !== 'checkedIn') return;
        const location = locations[operator.uid];
        if (getIsLocationStale(location, nowMs)) {
          summary.noSignal++;
          return;
        }
        const activity = getTruckActivity(location, [job]);
        if (activity.kind === 'atSite') {
          summary.onSiteBySiteId[activity.site.id] =
            (summary.onSiteBySiteId[activity.site.id] || 0) + 1;
        } else {
          summary.enRoute++;
        }
      });
  });
  return summary;
}

/**
 * Human-readable "x min ago" for a past timestamp.
 * @param {number} timestamp - Epoch milliseconds.
 * @param {number} [nowMs=Date.now()]
 * @returns {string}
 */
export function formatTimeAgo(
  timestamp: number,
  nowMs: number = Date.now()
): string {
  const minutes = Math.floor((nowMs - timestamp) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hr ${minutes % 60} min ago`;
}

// Operator marker colors, same as react-app (components/Map/Markers/OperatorMarker.js):
// COLORS.enabled fill, black stroke, COLORS.warning stroke when not tracking, black label
export const OPERATOR_MARKER_COLORS = {
  fill: '#ffffff',
  stroke: '#000000',
  strokeNotTracking: '#9b479f',
  label: '#000000',
} as const;

export type OperatorMarkerStyle = {
  fillColor: string;
  strokeColor: string;
  labelColor: string;
  opacity: number;
  isActive: boolean;
};

/**
 * Colors and opacity for an operator marker, following react-app's rules: an
 * operator is "active" when location sharing is on and the last fix is at most
 * 5 minutes old; inactive markers are drawn at half opacity, and the ring turns
 * the warning color when location sharing isn't on.
 * @param {object} params
 * @param {number} params.lastFixMs - Epoch milliseconds of the latest fix.
 * @param {boolean | null | undefined} params.isTracking - User's `isTracking` flag;
 *   undefined while the user document is still loading (treated as tracking to
 *   avoid flashing the warning color), null when the document has no flag
 *   (treated as not tracking, like react-app).
 * @param {number} [params.nowMs=Date.now()]
 * @returns {OperatorMarkerStyle}
 */
export function getOperatorMarkerStyle({
  lastFixMs,
  isTracking,
  nowMs = Date.now(),
}: {
  lastFixMs: number;
  isTracking: boolean | null | undefined;
  nowMs?: number;
}): OperatorMarkerStyle {
  const isTrackingOn = isTracking === undefined || isTracking === true;
  const isActive = isTrackingOn && nowMs - lastFixMs <= LOCATION_STALE_AFTER_MS;
  return {
    fillColor: OPERATOR_MARKER_COLORS.fill,
    strokeColor: isTrackingOn
      ? OPERATOR_MARKER_COLORS.stroke
      : OPERATOR_MARKER_COLORS.strokeNotTracking,
    labelColor: OPERATOR_MARKER_COLORS.label,
    opacity: isActive ? 1 : 0.5,
    isActive,
  };
}

export type DayKind = 'past' | 'today' | 'future';

/**
 * Whether a scheduler date is before, on or after the current day. Only today
 * has live truck positions; past days show GPS history and future days show
 * the plan.
 * @param {Date} date
 * @param {Date} [now=new Date()]
 * @returns {DayKind}
 */
export function getDayKind(date: Date, now: Date = new Date()): DayKind {
  const dayStart = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const diff = dayStart(date) - dayStart(now);
  return diff < 0 ? 'past' : diff > 0 ? 'future' : 'today';
}

export type SiteRoleKind = 'load' | 'dump' | 'both' | 'stop';

/**
 * What trucks do at a site across the jobs that use it: only load, only dump,
 * both (a load site for one job and a dump site for another, a candidate for
 * chaining trips), or only pass through as a middle stop.
 * @param {SiteRole[]} roles - The site's role in each job using it.
 * @returns {SiteRoleKind}
 */
export function getSiteRoleKind(roles: SiteRole[]): SiteRoleKind {
  const isLoad = roles.includes('load');
  const isDump = roles.includes('dump');
  if (isLoad && isDump) return 'both';
  if (isLoad) return 'load';
  if (isDump) return 'dump';
  return 'stop';
}
